import React from "react";
import { Button, CardinalLocation, Tooltip, TruncatedText } from "@hex-insights/core";
import {
	FormState,
	FormTooltipButton,
	FormTooltipButtonButtonProps,
	FormTooltipButtonTooltipProps,
	MultiSelectField,
	RadioButtonsInput,
	RadioField,
} from "@hex-insights/forms";
import { ClassYearFormat } from "../../../Format";
import { CampusSelectField, HomeRoomSectionSelectField } from "../../../Forms";
import { ClassYearUtilities } from "../../../Utilities";
import * as GuardianFilterFormValues from "../form-values";

const filterTooltipSideOrder: CardinalLocation[] = ["bottom", "top", "right", "left"];

type BaseFieldProps<K extends keyof GuardianFilterFormValues.FilterFormValues> = {
	formState: FormState<Pick<GuardianFilterFormValues.FilterFormValues, K>>;
};

export function StatusEQTooltipButton({ formState }: BaseFieldProps<"statusEQ">) {
	return (
		<FormTooltipButton formState={formState} sideOrder={filterTooltipSideOrder} autoFocus>
			{StatusEQTooltipButton.Tooltip}
			{StatusEQTooltipButton.Button}
		</FormTooltipButton>
	);
}

StatusEQTooltipButton.Tooltip = function ({
	formState,
}: FormTooltipButtonTooltipProps<Pick<GuardianFilterFormValues.FilterFormValues, "statusEQ">>) {
	return (
		<Tooltip isInteractable>
			<Tooltip.Body>
				<StatusEQ formState={formState} />
			</Tooltip.Body>
		</Tooltip>
	);
};

StatusEQTooltipButton.Button = function ({
	formState,
	onClick,
}: FormTooltipButtonButtonProps<Pick<GuardianFilterFormValues.FilterFormValues, "statusEQ">>) {
	const statusEQ = formState.formValues.statusEQ;

	return (
		<Button variant={statusEQ ? "primary" : "tertiary"} onClick={onClick}>
			{statusEQ ? formState.formFormatFunctions.statusEQ(statusEQ) : "Status"}
		</Button>
	);
};

const statusEQOptions = [
	{ value: null, label: "All" },
	{ value: "Enrolled" },
	{ value: "Future" },
	{ value: "Former" },
	{ value: "Not Enrolled" },
];

export function StatusEQ({ formState }: BaseFieldProps<"statusEQ">) {
	return (
		<RadioField
			formState={formState}
			name="statusEQ"
			label="Status"
			options={statusEQOptions}
			Input={RadioButtonsInput}
			blankValue={null}
			noOptional
		/>
	);
}

export function CampusIDsInTooltipButton({ formState }: BaseFieldProps<"campusIDsIn">) {
	return (
		<FormTooltipButton formState={formState} sideOrder={filterTooltipSideOrder} autoFocus>
			{CampusIDsInTooltipButton.Tooltip}
			{CampusIDsInTooltipButton.Button}
		</FormTooltipButton>
	);
}

CampusIDsInTooltipButton.Tooltip = function ({
	formState,
}: FormTooltipButtonTooltipProps<Pick<GuardianFilterFormValues.FilterFormValues, "campusIDsIn">>) {
	return (
		<Tooltip isInteractable>
			<Tooltip.Body>
				<CampusIDsIn formState={formState} />
			</Tooltip.Body>
		</Tooltip>
	);
};

CampusIDsInTooltipButton.Button = function ({
	formState,
	onClick,
}: FormTooltipButtonButtonProps<Pick<GuardianFilterFormValues.FilterFormValues, "campusIDsIn">>) {
	const campusIDsIn = formState.formValues.campusIDsIn;

	return (
		<Button variant={campusIDsIn.length > 0 ? "primary" : "tertiary"} onClick={onClick}>
			<TruncatedText style={{ maxWidth: "10rem" }}>
				{campusIDsIn.length > 0 ? `${formState.formFormatFunctions.campusIDsIn(campusIDsIn)}` : "Campus"}
			</TruncatedText>
		</Button>
	);
};

export function CampusIDsIn({ formState }: BaseFieldProps<"campusIDsIn">) {
	return <CampusSelectField.MultiSelect formState={formState} name="campusIDsIn" label="Campuses" noOptional />;
}

export function GraduationYearInTooltipButton({ formState }: BaseFieldProps<"graduationYearIn">) {
	return (
		<FormTooltipButton formState={formState} sideOrder={filterTooltipSideOrder} autoFocus>
			{GraduationYearInTooltipButton.Tooltip}
			{GraduationYearInTooltipButton.Button}
		</FormTooltipButton>
	);
}

GraduationYearInTooltipButton.Tooltip = function ({
	formState,
}: FormTooltipButtonTooltipProps<Pick<GuardianFilterFormValues.FilterFormValues, "graduationYearIn">>) {
	return (
		<Tooltip isInteractable>
			<Tooltip.Body>
				<GraduationYearIn formState={formState} />
			</Tooltip.Body>
		</Tooltip>
	);
};

GraduationYearInTooltipButton.Button = function ({
	formState,
	onClick,
}: FormTooltipButtonButtonProps<Pick<GuardianFilterFormValues.FilterFormValues, "graduationYearIn">>) {
	const graduationYearIn = formState.formValues.graduationYearIn;

	return (
		<Button variant={graduationYearIn.length > 0 ? "primary" : "tertiary"} onClick={onClick}>
			<TruncatedText style={{ maxWidth: "10rem" }}>
				{graduationYearIn.length > 0
					? `${formState.formFormatFunctions.graduationYearIn(graduationYearIn)}`
					: "Class Year"}
			</TruncatedText>
		</Button>
	);
};

export function GraduationYearIn({ formState }: BaseFieldProps<"graduationYearIn">) {
	const { loading, data: classYearsData } = ClassYearUtilities.useClassYearsMap();
	const options = React.useMemo(() => {
		if (!classYearsData) {
			return [];
		}

		return classYearsData.classYearConnection.edges.map(({ node }) => ({
			value: node.graduationYear,
			label: ClassYearFormat.name(node),
		}));
	}, [classYearsData]);

	return (
		<MultiSelectField
			formState={formState}
			name="graduationYearIn"
			label="Class Years"
			isLoading={loading}
			options={options}
			noOptional
		/>
	);
}

export function HomeRoomSectionIDInTooltipButton({ formState }: BaseFieldProps<"homeRoomSectionIDIn">) {
	return (
		<FormTooltipButton formState={formState} sideOrder={filterTooltipSideOrder} autoFocus>
			{HomeRoomSectionIDInTooltipButton.Tooltip}
			{HomeRoomSectionIDInTooltipButton.Button}
		</FormTooltipButton>
	);
}

HomeRoomSectionIDInTooltipButton.Tooltip = function ({
	formState,
}: FormTooltipButtonTooltipProps<Pick<GuardianFilterFormValues.FilterFormValues, "homeRoomSectionIDIn">>) {
	return (
		<Tooltip isInteractable>
			<Tooltip.Body>
				<HomeRoomSectionIDIn formState={formState} />
			</Tooltip.Body>
		</Tooltip>
	);
};

HomeRoomSectionIDInTooltipButton.Button = function ({
	formState,
	onClick,
}: FormTooltipButtonButtonProps<Pick<GuardianFilterFormValues.FilterFormValues, "homeRoomSectionIDIn">>) {
	const homeRoomSectionIDIn = formState.formValues.homeRoomSectionIDIn;

	return (
		<Button variant={homeRoomSectionIDIn.length > 0 ? "primary" : "tertiary"} onClick={onClick}>
			<TruncatedText style={{ maxWidth: "10rem" }}>
				{homeRoomSectionIDIn.length > 0
					? `${formState.formFormatFunctions.homeRoomSectionIDIn(homeRoomSectionIDIn)}`
					: "Home Room Section"}
			</TruncatedText>
		</Button>
	);
};

export function HomeRoomSectionIDIn({ formState }: BaseFieldProps<"homeRoomSectionIDIn">) {
	return (
		<HomeRoomSectionSelectField.MultiSelect
			formState={formState}
			name="homeRoomSectionIDIn"
			label="Home Room Sections"
			noOptional
		/>
	);
}
